import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IconCheck } from "@/components/BrandGraphics";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";

// Maillage interne : toutes les pages publiques thématiques
const RELATED_PAGES = [
  { to: "/permis", label: "Permis B" },
  { to: "/permis-accelere", label: "Permis accéléré" },
  { to: "/transfert-auto-ecole", label: "Changer d'auto-école" },
  { to: "/qui-sommes-nous", label: "Qui sommes-nous ?" },
  { to: "/contact", label: "Nous contacter" },
];

export function PublicPage({ eyebrow, title, intro, bullets = [], ctaLabel = "Recevoir mes propositions", seoTitle, seoDescription }) {
  useSeo({ title: seoTitle || `${title} | PlanPermis`, description: seoDescription || intro });
  const location = useLocation();
  const related = RELATED_PAGES.filter((p) => p.to !== location.pathname);
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="public-logo"><Logo className="h-10" /></Link>
          <div className="flex items-center gap-fib-4 text-gr-sm font-semibold">
            <Link to="/blog" data-testid="public-blog-link" className="hidden text-neutral-600 hover:text-brand sm:inline">Blog</Link>
            <Link to="/ambassadeur" data-testid="public-ambassador-link" className="hidden text-neutral-600 hover:text-brand sm:inline">Devenir ambassadeur</Link>
            <Link to="/inscription-auto-ecole" className="hidden text-neutral-600 hover:text-brand sm:inline">Devenir partenaire</Link>
            <Link to="/connexion" data-testid="public-login-link" className="text-neutral-600 hover:text-brand">Connexion</Link>
            <Link to="/reserver" className="rounded-full bg-brand px-fib-4 py-fib-2 text-white hover:opacity-90">Faire ma demande</Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <section className="mx-auto max-w-3xl px-6 py-fib-7 sm:py-fib-8">
          {eyebrow && <p className="text-gr-xs font-bold uppercase tracking-[0.2em] text-brand">{eyebrow}</p>}
          <h1 className="mt-fib-3 text-gr-2xl font-extrabold tracking-tight text-neutral-900 sm:text-gr-3xl text-balance">{title}</h1>
          <p className="mt-fib-4 measure text-gr-lg text-neutral-600">{intro}</p>

          <div className="mt-fib-6 grid gap-fib-4 sm:grid-cols-2">
            {bullets.map((b, i) => (
              <div key={i} className="flex items-start gap-fib-3 rounded-xl border border-neutral-200 bg-white p-fib-5">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand/10">
                  <IconCheck className="h-4 w-4 text-brand" />
                </span>
                <div>
                  <p className="font-bold text-neutral-900">{b.title}</p>
                  <p className="mt-1 text-gr-sm text-neutral-600">{b.sub}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-fib-7 rounded-2xl border border-brand/30 bg-brand/5 p-fib-6 text-center">
            <p className="text-gr-lg font-bold text-neutral-900">Prêt(e) à avancer ?</p>
            <p className="mt-fib-2 text-gr-sm text-neutral-600">Simple et sans engagement. Tu reçois par email les coordonnées de l'auto-école adaptée à ta demande, et c'est toi qui la contactes (une seule ou plusieurs, selon ton choix).</p>
            <Link to="/reserver" data-testid="public-cta"
              className="mt-fib-4 inline-flex items-center gap-fib-2 rounded-full bg-brand px-fib-6 py-fib-3 text-gr-base font-bold text-white hover:opacity-90">
              {ctaLabel} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Maillage interne : pages liées */}
          <nav aria-label="Pages liées" className="mt-fib-7 border-t border-neutral-200 pt-fib-5" data-testid="related-links">
            <p className="text-gr-xs font-bold uppercase tracking-[0.2em] text-neutral-400">À découvrir aussi</p>
            <div className="mt-fib-3 flex flex-wrap gap-fib-2">
              {related.map((p) => (
                <Link key={p.to} to={p.to} data-testid={`related-link-${p.to.replace(/\//g, "")}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-fib-4 py-fib-2 text-gr-sm font-semibold text-neutral-700 transition-colors hover:border-brand hover:text-brand">
                  {p.label} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ))}
            </div>
          </nav>
        </section>
      </main>
      <Footer />
    </div>
  );
}
